import { useEffect } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * User preferences — theme + UI language. Persisted to localStorage so the
 * choice survives logout/login on the same device. Backend persistence
 * could be layered on later by syncing this store with `PUT /profile`.
 */

export type AppLanguage = 'en' | 'es';

export type ThemeId = 'volt' | 'nebula' | 'orchid' | 'mist';

export interface ThemeMeta {
  id: ThemeId;
  label: string;
  mode: 'dark' | 'light';
  /** Preview swatches shown in the menu (bg, surface, accent). */
  preview: { bg: string; surface: string; accent: string };
}

/**
 * Themes derive from the Xamplin brand palette (5 colors):
 *   #1B0F2B dark · #6C1CD3 deep purple · #B262F5 violet ·
 *   #E2DFF2 light lavender · #C6D93B lime.
 * Each combo maps to one selectable theme (2 dark + 2 light).
 */
export const THEMES: ThemeMeta[] = [
  { id: 'volt', label: 'Volt', mode: 'dark', preview: { bg: '#1b0f2b', surface: '#251640', accent: '#c6d93b' } },
  { id: 'nebula', label: 'Nebula', mode: 'dark', preview: { bg: '#1b0f2b', surface: '#241638', accent: '#6c1cd3' } },
  { id: 'orchid', label: 'Orchid', mode: 'light', preview: { bg: '#e2dff2', surface: '#ffffff', accent: '#b262f5' } },
  { id: 'mist', label: 'Mist', mode: 'light', preview: { bg: '#f3f1fa', surface: '#ffffff', accent: '#6c1cd3' } },
];

const THEME_IDS: ThemeId[] = ['volt', 'nebula', 'orchid', 'mist'];

/** Default theme — dark + deep-purple, closest to the brand identity. */
const DEFAULT_THEME: ThemeId = 'nebula';

export const LANGUAGES: { id: AppLanguage; label: string; flag: string }[] = [
  { id: 'en', label: 'English', flag: 'EN' },
  { id: 'es', label: 'Español', flag: 'ES' },
];

interface PrefsState {
  theme: ThemeId;
  language: AppLanguage;
  setTheme: (id: ThemeId) => void;
  setLanguage: (id: AppLanguage) => void;
}

export const usePrefsStore = create<PrefsState>()(
  persist(
    (set) => ({
      theme: DEFAULT_THEME,
      language: 'en',
      setTheme: (theme) => set({ theme }),
      setLanguage: (language) => set({ language }),
    }),
    {
      name: 'xotski.prefs',
      version: 1,
      // Existing devices persisted a now-removed theme id (ink/cobalt/…).
      // Map any unknown id back to the default so <html data-theme> never
      // points at a theme block that no longer exists.
      migrate: (state) => {
        const s = state as Partial<PrefsState> | undefined;
        const theme = s?.theme && THEME_IDS.includes(s.theme) ? s.theme : DEFAULT_THEME;
        return { ...s, theme } as PrefsState;
      },
    },
  ),
);

/**
 * Applies the current theme + language to <html> so CSS variable swaps take
 * effect everywhere. Mounted once at the app shell level.
 */
export function ThemeApplier() {
  const theme = usePrefsStore((s) => s.theme);
  const language = usePrefsStore((s) => s.language);
  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    root.lang = language;
  }, [theme, language]);
  return null;
}
